import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { gql } from 'graphql-tag';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DataService, Dialog, NotificationService, SharedModule } from '@vendure/admin-ui/core';
import { ErpLingXingStoreSelection } from '../../common/ui/ui-types';

const GET_ERP_LINGXING_STORES = gql`
    query GetErpLingXingStores($type: String!, $appId: String!, $appSecret: String!) {
        getErpLingXingStores(type: $type, appId: $appId, appSecret: $appSecret) {
            storeId
            storeName
        }
    }
`;

@Component({
    selector: 'vdr-update-lingxing-store-dialog',
    template: `
        <ng-template vdrDialogTitle>Update Platform LingXing Store</ng-template>

        <div *ngIf="loading" class="text-center">
            <p>Loading...</p>
        </div>

        <div *ngIf="error" class="alert alert-danger">{{ error }}</div>

        <form [formGroup]="form" *ngIf="!error && stores$ | async as stores">
            <vdr-form-field label="Available Stores">
                <select formControlName="selectedStore">
                    <option [ngValue]="null" disabled>-- Select a store --</option>
                    <option *ngFor="let store of stores" [ngValue]="store">
                        {{ store.storeName }}
                    </option>
                </select>
            </vdr-form-field>
        </form>

        <ng-template vdrDialogButtons>
            <button type="button" class="btn" (click)="cancel()">Cancel</button>
            <button
                type="button"
                class="btn btn-primary"
                [disabled]="form.invalid || form.pristine"
                (click)="submit()"
            >
                Update Store
            </button>
        </ng-template>
    `,
    standalone: true,
    imports: [SharedModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateLingXingStoreDialogComponent implements Dialog<ErpLingXingStoreSelection>, OnInit {
    // These values will be passed in from the parent component
    type: string;
    appId: string;
    appSecret: string;

    form: FormGroup;
    resolveWith: (result?: ErpLingXingStoreSelection) => void;

    stores$: Observable<ErpLingXingStoreSelection[]>;
    loading = true;
    error: string | null = null;

    constructor(
        private formBuilder: FormBuilder,
        private dataService: DataService,
        private notificationService: NotificationService,
    ) {
        this.form = this.formBuilder.group({
            selectedStore: [null, Validators.required],
        });
    }

    ngOnInit() {
        this.stores$ = this.dataService
            .query(GET_ERP_LINGXING_STORES, {
                type: this.type,
                appId: this.appId,
                appSecret: this.appSecret,
            })
            .mapSingle((result:any) => {
                this.loading = false;
                console.log('Fetched stores:', result);
                return result.getErpLingXingStores;
            })
            .pipe(
                catchError(err => {
                    this.loading = false;
                    this.error = `Failed to fetch stores: ${err.message}`;
                    this.notificationService.error(this.error);
                    return of([]);
                }),
            );
    }

    cancel() {
        this.resolveWith();
    }

    submit() {
        if (this.form.valid && this.form.value.selectedStore) {
            // The form value contains the entire selected store object because we used [ngValue]
            const selection: ErpLingXingStoreSelection = this.form.value.selectedStore;
            this.resolveWith(selection);
        }
    }
}